import React, { useState } from 'react';
import { TableColumn } from 'react-data-table-component';
import { ApiGeneralItem, BasicTab } from '../config/type';
import { EXCLUDES, FIELD_ORDER, LABELS } from '../config/constants';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button as RSButton } from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';

/* ===== Helpers di formattazione ===== */
export const fmtVal = (v: unknown) => (v === null || v === undefined || v === '' ? '-' : String(v));

export const fmtAge = (v: string | number | null) =>
  v === null ? '-' : Number.isFinite(+v) ? String(+Number(v).toFixed(0)) : fmtVal(v);

export const onlyDigits = (v: string) => v.replace(/[^\d]/g, '');

export const fmtPhone = (v: string | null) => {
  if (!v) return '-';
  let digits = onlyDigits(String(v));
  if (!digits) return '-';
  if (digits.startsWith('39') && digits.length > 10) {
    digits = digits.substring(2);
  }

  return digits;
};

/** Costruisce link wa.me. Qui assumo prefisso Italia 39: se serve generalizzare, passalo come prop. */
export const buildWhatsAppHref = (phone: string | null, countryCode = '39') => {
  if (!phone) return null;
  const digits = onlyDigits(String(phone));
  if (!digits) return null;
  return `https://wa.me/${countryCode}${digits}`;
};

export const buildTelHref = (phone: string | null) => {
  if (!phone) return null;
  const digits = onlyDigits(String(phone));
  if (!digits) return null;
  return `tel:${digits}`;
};


export const birthdaySortValue = (value: string | null) => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const tokens = String(value)
    .split(/[^\d]/)
    .filter(Boolean)
    .map((token) => Number(token));

  if (tokens.length < 2) return Number.MAX_SAFE_INTEGER;
  const [day, month] = tokens;
  if (!Number.isFinite(day) || !Number.isFinite(month)) return Number.MAX_SAFE_INTEGER;
  return month * 100 + day; 
};


export const PhoneCell: React.FC<{ phone: string | null }> = ({ phone }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((v) => !v);

  const formattedPhone = fmtPhone(phone);
  const waHref = buildWhatsAppHref(formattedPhone, '39'); 
  const telHref = buildTelHref(formattedPhone);

  // se non ho un numero valido, mostro solo testo
  if (!waHref && !telHref) {
    return <span>{formattedPhone}</span>;
  }

  const onClickCell = (e: React.MouseEvent) => {
    e.stopPropagation(); 
    toggle();
  };

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(onlyDigits(String(phone)));
      toast.success('Numero copiato');
    } catch {
      //
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClickCell}
        className="btn btn-link p-0 text-decoration-none"
        style={{ cursor: 'pointer' }}
      >
        {formattedPhone}
      </button>
      {/* Modal azioni */}
      <Modal isOpen={open} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Contatta</ModalHeader>
        <ModalBody>
          <div className="d-flex flex-column gap-2">
            <div><strong>Numero:</strong> {formattedPhone}</div>
            <div className="text-muted">Scegli l'azione da eseguire:</div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between flex-wrap gap-2">
          <div className="d-flex gap-2">
            {waHref && (
              <RSButton
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(waHref, '_blank', 'noopener,noreferrer');
                }}
              >
                WhatsApp
              </RSButton>
            )}
            {telHref && (
              <a
                href={telHref}
                onClick={(e) => e.stopPropagation()}
                className="btn btn-primary"
              >
                Chiama
              </a>
            )}
            <RSButton color="secondary" outline onClick={onCopy}>
              Copia numero
            </RSButton>
          </div>
          <Btn color="light" onClick={(e: any) => { e.stopPropagation(); toggle(); }}>
            Chiudi
          </Btn>
        </ModalFooter>
      </Modal>
    </>
  );
};

/* ===== Costruzione colonne ===== */
export function buildColumns(tab: BasicTab): TableColumn<ApiGeneralItem>[] {
  const exclude = new Set(EXCLUDES[tab]);

  const makeCell = (key: keyof ApiGeneralItem) => {
    switch (key) {

    case 'consumer_id':
      return (row: ApiGeneralItem) => (
        <span className="d-flex align-items-center gap-2">
          {row.consumer_id}
          {row.nota === 1 && (
            <i className="fa fa-regular fa-comment" title="Ha una nota"></i>
          )}
        </span>
      );

      case 'avg_age':
        return (row: ApiGeneralItem) => fmtAge(row.avg_age);

      case 'telefono':
        return (row: ApiGeneralItem) => <PhoneCell phone={row.telefono ?? null} />;

      default:
        return (row: ApiGeneralItem) => fmtVal(row[key]);
    }
  };

  const makeSelector = (key: keyof ApiGeneralItem) => {
    switch (key) {
      case 'data_nascita':
        return (row: ApiGeneralItem) => birthdaySortValue(row.data_nascita);
      default:
        return (row: ApiGeneralItem) => {
          const value = row[key];
          if (value === null || value === undefined) return '';
          return typeof value === 'number' ? value : String(value);
        };
    }
  };

  return FIELD_ORDER
    .filter((k) => !exclude.has(k))
    .map<TableColumn<ApiGeneralItem>>((k) => ({
      name: LABELS[k],
      selector: makeSelector(k),                   
      cell: makeCell(k),                             
      sortable: true,
      sortField: String(k),
      right: ['mese_nascita','giorno_nascita','rdvID'].includes(k as string),
      width: k === 'consumer_id' ? '150px' : k === 'telefono' ? '180px' : undefined,
    }));
}
